import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { ShieldCheck, Sparkles, AlertCircle, CheckCircle2, ArrowRight } from "lucide-react";
import VeyraLogo from "@/components/brand/VeyraLogo";
import { api } from "@/lib/api";
import { useAuth } from "@/store/AuthContext";
import { toast } from "sonner";

export default function InviteAcceptPage() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const { setUser } = useAuth();

  const [loading, setLoading] = useState(true);
  const [validationError, setValidationError] = useState(null);
  const [invitedEmail, setInvitedEmail] = useState("");

  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);

  useEffect(() => {
    if (!token) {
      setValidationError("No se especificó ningún token en el enlace de invitación.");
      setLoading(false);
      return;
    }

    let isMounted = true;
    (async () => {
      try {
        const res = await api.validateInvitation(token);
        if (isMounted) {
          setInvitedEmail(res.email || "");
          setLoading(false);
        }
      } catch (err) {
        if (!isMounted) return;
        const detail = err.message || "";
        if (detail === "invitation_expired") {
          setValidationError("Este enlace de invitación ha caducado (superó el límite de 48 horas). Solicita un nuevo enlace al Propietario.");
        } else if (detail === "invitation_revoked") {
          setValidationError("Esta invitación fue revocada por el Propietario del estudio.");
        } else if (detail === "invitation_already_used") {
          setValidationError("Esta invitación ya fue utilizada anteriormente. Si ya creaste tu cuenta, puedes iniciar sesión directamente.");
        } else if (detail === "invitation_not_found") {
          setValidationError("El enlace de invitación no es válido o está incompleto.");
        } else {
          setValidationError("No se pudo validar la invitación: " + detail);
        }
        setLoading(false);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);

    if (password.length < 8) {
      setFormError("La contraseña debe tener al menos 8 caracteres.");
      return;
    }
    if (password !== confirmPassword) {
      setFormError("Las contraseñas no coinciden.");
      return;
    }

    setSubmitting(true);
    try {
      const newUser = await api.acceptInvitation({
        token,
        name: name.trim(),
        password,
      });
      setUser(newUser);
      toast.success("¡Bienvenido al estudio!");
      navigate("/", { replace: true });
    } catch (err) {
      if (err.message === "email_already_registered") {
        setFormError("Este correo ya está registrado en el estudio. Inicia sesión directamente.");
      } else if (err.message === "invalid_or_expired_invitation") {
        setFormError("La invitación ya no es válida o acaba de expirar.");
      } else {
        setFormError("Error al aceptar invitación: " + (err.message || "desconocido"));
      }
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#101010] p-4" data-testid="invite-accept-page">
      <div className="w-full max-w-md rounded-xl border border-[#2b2b2b] bg-[#161616] p-6 shadow-2xl md:p-8">
        <div className="flex flex-col items-center text-center">
          <VeyraLogo size={32} />
          <div className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-[#f5b544]/40 bg-[#f5b544]/10 px-3 py-1 text-xs font-medium text-[#f5b544]">
            <Sparkles size={13} />
            Invitación al Estudio
          </div>
          <h1 className="mt-3 text-xl font-bold text-[#f4f1ec]">Acceso Independiente</h1>
          <p className="mt-1 text-xs text-[#a3a3a3]">
            Configura tu cuenta para comenzar a crear tus propios proyectos cinematográficos.
          </p>
        </div>

        {loading ? (
          <div className="mt-8 flex flex-col items-center justify-center py-8 text-center text-xs text-[#a3a3a3]">
            <div className="h-7 w-7 animate-spin rounded-full border-2 border-[#303030] border-t-[#f5b544]" />
            <span className="mt-3">Verificando enlace de invitación...</span>
          </div>
        ) : validationError ? (
          <div className="mt-6 flex flex-col gap-4">
            <div className="flex items-start gap-3 rounded-lg border border-rose-500/30 bg-rose-500/10 p-4 text-xs text-rose-300">
              <AlertCircle size={18} className="mt-0.5 shrink-0 text-rose-400" />
              <div>
                <div className="font-semibold text-rose-400">Invitación no disponible</div>
                <p className="mt-1 text-rose-300/90 leading-relaxed">{validationError}</p>
              </div>
            </div>
            <Link
              to="/login"
              className="v-btn-primary v-btn-sm flex w-full items-center justify-center gap-2 py-2.5 text-center"
            >
              Ir a Iniciar Sesión <ArrowRight size={14} />
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="mt-6 flex flex-col gap-4">
            <div>
              <label className="text-xs font-medium text-[#a3a3a3]">Correo invitado</label>
              <input
                type="email"
                disabled
                value={invitedEmail}
                className="v-input mt-1 w-full bg-[#1e1e1e] font-mono text-xs text-[#f4f1ec] opacity-80 cursor-not-allowed"
              />
            </div>

            <div>
              <label className="text-xs font-medium text-[#a3a3a3]">Nombre completo o artístico</label>
              <input
                type="text"
                required
                data-testid="invite-accept-name"
                placeholder="Ej. Sofia Leone"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="v-input mt-1 w-full text-sm"
              />
            </div>

            <div>
              <label className="text-xs font-medium text-[#a3a3a3]">Contraseña (mínimo 8 caracteres)</label>
              <input
                type="password"
                required
                autoComplete="new-password"
                data-testid="invite-accept-password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="v-input mt-1 w-full text-sm"
              />
            </div>

            <div>
              <label className="text-xs font-medium text-[#a3a3a3]">Confirmar contraseña</label>
              <input
                type="password"
                required
                autoComplete="new-password"
                data-testid="invite-accept-confirm-password"
                placeholder="••••••••"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="v-input mt-1 w-full text-sm"
              />
            </div>

            {formError && (
              <div className="rounded-lg border border-rose-500/30 bg-rose-500/10 p-3 text-xs text-rose-400">
                {formError}
              </div>
            )}

            <div className="rounded-lg border border-[#262626] bg-[#121212] p-3 text-[11px] leading-relaxed text-[#787878]">
              Trabajarás en tu propio espacio de trabajo con proyectos, personajes y medios independientes. En esta fase
              (Bloque 1), las funciones de generación con IA se encuentran en preparación para el Bloque 2 con claves y
              presupuesto propios.
            </div>

            <button
              type="submit"
              disabled={submitting || !name.trim() || !password}
              data-testid="invite-accept-submit"
              className="v-btn-primary v-btn-sm mt-2 flex w-full items-center justify-center gap-2 py-2.5 font-medium"
            >
              <CheckCircle2 size={16} />
              {submitting ? "Creando cuenta..." : "Aceptar Invitación y Entrar"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
